import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '../../services/storage/local-storage.service';
import { TableSort } from '../../components/datatable/types/sort.type';

interface TableStateBranch {
  sorts?: Array<TableSort>;
}

type TableStateEntry = TableStateBranch | Array<TableSort> | undefined;

interface TableStateStore {
  sorts?: Array<TableSort>;
  [key: string]: TableStateEntry;
}

function isTableStateBranch(value: TableStateEntry): value is TableStateBranch {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@Injectable({
  providedIn: 'root'
})
export class TableStateService {
  private readonly localStorageService = inject(LocalStorageService);

  setSorts(stateId: string, subState: string | undefined, sorts?: Array<TableSort>): void {
    const state = this.readState(stateId);

    if (subState === undefined) {
      state.sorts = sorts;
      this.localStorageService.setItem(stateId, state);
    } else {
      const branch = isTableStateBranch(state[subState]) ? state[subState] : {};
      branch.sorts = sorts;
      state[subState] = branch;
      this.localStorageService.setItem(stateId, state);
    }
  }

  getSorts(stateId: string, subState?: string): Array<TableSort> | undefined {
    if (subState !== undefined) {
      return this.getSubState(stateId, subState);
    }
    return this.getState(stateId);
  }

  resetSort(stateId: string, subState?: string): void {
    this.setSorts(stateId, subState);
  }

  private getState(stateId: string): Array<TableSort> | undefined {
    const state = this.readState(stateId);

    if (state?.sorts !== undefined && Array.isArray(state.sorts)) {
      return state.sorts;
    }

    return undefined;
  }

  private getSubState(stateId: string, subState: string): Array<TableSort> | undefined {
    const state = this.readState(stateId);
    const subStateValue = state[subState];

    if (isTableStateBranch(subStateValue)
      && subStateValue.sorts !== undefined
      && Array.isArray(subStateValue.sorts)) {
      return subStateValue.sorts;
    }

    return undefined;
  }

  private readState(stateId: string): TableStateStore {
    return this.localStorageService.getItem<TableStateStore>(stateId) ?? {};
  }
}
